// src/components/GenreCarousel.jsx
import React, { useEffect, useState } from "react";
import "../styles/Genre_carousel.css";
import bigLeftArrow from '../images/bigLeftArrow.png';
import bigRightArrow from '../images/bigRightArrow.png';
import { useNavigate } from "react-router-dom";
import { fetchAllGenreBooks } from "../services/AllServices.js";
import { getToken } from '../storage/Storage'; 
import { speakText } from './utils/speechUtils';

const GenreCarousel = ({ heading, genre_carousel_images }) => {
  const [currentIdx, setCurrentIdx] = useState(0);
  const [genreBooks, setGenreBooks] = useState({});
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);
  const navigate = useNavigate();

  const maxIndex = Math.max(0, genre_carousel_images.length - 5);
  const showArrows = genre_carousel_images.length > 5;
  const isBeginning = currentIdx === 0;
  const isEnd = currentIdx >= maxIndex;

  useEffect(() => {
    const fetchToken = async () => {
      const storedToken = await getToken();
      setToken(storedToken);
    };
    fetchToken();

    const fetchGenreBooks = async () => {
      try {
        const response = await fetchAllGenreBooks();
        setGenreBooks(response.data);
        setLoading(false);
      } catch (error) {
        console.error("Failed to fetch genre books:", error);
        setLoading(false);
      }
    };
    fetchGenreBooks();
  }, []);

  const handleItemClick = (itemId) => {
    if (!token) {
      navigate('/signin');
    } else {
      navigate(`/book-info/${heading}/${itemId}`, { state: { genreBooks } });
    }
  };

  const prevSlide = () => {
    setCurrentIdx((prevIdx) => Math.max(0, prevIdx - 1));
  };

  const nextSlide = () => {
    setCurrentIdx((prevIdx) => Math.min(maxIndex, prevIdx + 1));
  };

  // keep a simple handler for Enter key to ensure consistent behaviour
  const handleKeyPress = (event, action) => {
    if (event.key === "Enter") {
      action();
    }
  };

  const handleFocusOnGenre = () => {
    speakText(`${heading} Genre`);
  };

  const handleFocusOnBook = (bookTitle) => {
    speakText(`${bookTitle}`);
  };

  const handleFocusOnArrow = (direction) => {
    speakText(`Move ${direction} through the genre carousel`);
  };

  if (!Array.isArray(genre_carousel_images) || genre_carousel_images.length === 0) {
    return null;
  }

  return (
    <div className="carousel__wrap">
      <div className="carousel__inner">

        <button
          className={`carousel__btn ${isBeginning ? 'disabled' : ''}`}
          onClick={prevSlide}
          onKeyDown={(e) => handleKeyPress(e, prevSlide)}
          disabled={isBeginning}
          tabIndex={isBeginning ? -1 : 0}
          onFocus={() => handleFocusOnArrow("backward")}
          aria-label="Previous items"
        >
          <div className="carousel__btn--prev">
            {showArrows && (
              <img src={bigLeftArrow} alt="Previous" className="carousel__btn-arrow" />
            )}
          </div>
        </button>

        <div className="carousel__container">
          <div
            className="genre_heading"
            onFocus={handleFocusOnGenre}
            tabIndex={0}
            role="heading"
            aria-level="2"
          >
            {heading}
          </div>

          <div
            className="carousel__slide-list"
            style={{ transform: `translateX(-${currentIdx * (10.3 + 2)}vw)` }}
          >
            {loading ? (
              <div className="carousel__skeleton-loader" aria-hidden="true">
                {[...Array(7)].map((_, index) => (
                  <div key={index} className="carousel__skeleton-item"></div>
                ))}
              </div>
            ) : (
              genre_carousel_images.map((item, index) => (
                <div
                  key={index}
                  className="carousel__slide-item"
                >
                  {/* semantic button replacing role="button" div */}
                  <button
                    className="carousel__item-button"
                    type="button"
                    onClick={() => handleItemClick(item.id)}
                    onKeyDown={(e) => {
                      // support Enter/Space explicitly, prevent scrolling on Space
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleItemClick(item.id);
                      }
                    }}
                    onFocus={() => handleFocusOnBook(item.title)}
                    aria-label={`${item.title} - open book details`}
                  >
                    <img
                      src={item.image}
                      alt={item.title}
                      className="carousel__slide-thumbnail"
                    />
                    <div className="hover_container" aria-hidden="true">
                      <div className="hover_book_name">{item.title}</div>
                      <div className="hover_book_author">
                        {item.author_list?.length > 0 ? item.author_list.join(", ") : ""}
                      </div>
                    </div>
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        <button
          className={`carousel__btn ${isEnd ? 'disabled' : ''}`}
          onClick={nextSlide}
          onKeyDown={(e) => handleKeyPress(e, nextSlide)}
          disabled={isEnd}
          tabIndex={isEnd ? -1 : 0}
          onFocus={() => handleFocusOnArrow("forward")}
          aria-label="Next items"
        >
          <div className="carousel__btn--next">
            {showArrows && (
              <img src={bigRightArrow} alt="Next" className="carousel__btn-arrow" />
            )}
          </div>
        </button>

      </div>
    </div>
  );
};

export default GenreCarousel;
